/**
 * prefs.ts — local user prefs persisted to localStorage. Mirrors the
 * Options panel from the legacy SWF (DefineSprite_376_optionsWindow):
 *   - Show Timestamps
 *   - Auto-scroll Bottom
 *   - Play Effects  (Easter-egg sprite overlays + UI sounds)
 *   - Play Sounds   (soundboard playback on sound_message)
 *
 * "Mac <> Fix" from the legacy was a UTF-8 transcoding workaround that
 * isn't needed in HTML5; we keep the checkbox for faithfulness but it
 * is a no-op.
 *
 * All keys are namespaced under `prochat.prefs.*` so localStorage stays
 * tidy.
 */

const NS = 'prochat.prefs.';

export interface Prefs {
    showTimestamps: boolean;
    autoscrollBottom: boolean;
    macFix: boolean;            // legacy no-op
    playEffects: boolean;
    playSounds: boolean;
    /** User-chosen colour override; null = use rank colour. */
    colorOverride: string | null;
    /** Persisted ignore list (usernames, case-sensitive as stored). */
    ignoreList: string[];
}

export const DEFAULT_PREFS: Prefs = {
    showTimestamps:   true,
    autoscrollBottom: true,
    macFix:           false,
    playEffects:      true,
    playSounds:       true,
    colorOverride:    null,
    ignoreList:       [],
};

function read<T extends keyof Prefs>(k: T, fallback: Prefs[T]): Prefs[T] {
    try {
        const raw = localStorage.getItem(NS + k);
        if (raw == null) return fallback;
        return JSON.parse(raw) as Prefs[T];
    } catch { return fallback; }
}

function write<T extends keyof Prefs>(k: T, v: Prefs[T]): void {
    try { localStorage.setItem(NS + k, JSON.stringify(v)); } catch { /* ignore */ }
}

export function loadPrefs(): Prefs {
    return {
        showTimestamps:   read('showTimestamps',   DEFAULT_PREFS.showTimestamps),
        autoscrollBottom: read('autoscrollBottom', DEFAULT_PREFS.autoscrollBottom),
        macFix:           read('macFix',           DEFAULT_PREFS.macFix),
        playEffects:      read('playEffects',      DEFAULT_PREFS.playEffects),
        playSounds:       read('playSounds',       DEFAULT_PREFS.playSounds),
        colorOverride:    read('colorOverride',    DEFAULT_PREFS.colorOverride),
        ignoreList:       read('ignoreList',       DEFAULT_PREFS.ignoreList),
    };
}

export function savePref<K extends keyof Prefs>(k: K, v: Prefs[K]): void {
    write(k, v);
}

/** Dispatch a CustomEvent so components can refresh on save. */
export function emitChange(): void {
    try { window.dispatchEvent(new CustomEvent('prochat-prefs-change')); } catch { /* SSR/test guard */ }
}
